import { AutogenLayoutLoadTestListeners } from "./autogen/AutogenLayoutLoadTestListeners";
import { ClickEvent } from "@mcleod/components";
import { LogManager, MessageSubscriber } from "@mcleod/core";
import { Snackbar } from "@mcleod/components";

const log = LogManager.getLogger("environment-manager.EnvironmentManager");

export class LoadTestListeners extends AutogenLayoutLoadTestListeners {

    // Integer fields to track message counts for each topic
    private clientListCount = 0;
    private brokerageMovementsCount = 0;
    private availableMoveCount = 0;
    private equipMoveCount = 0;
    private loadTenderExpressRemoteCount = 0;
    private orderPayablesCacheCount = 0;
    private invoiceProcessingCacheCount = 0;
    private environmentManagementMovementsCount = 0;
    private partialShipmentPlanningOrdersCount = 0;
    private partialShipmentPlanningManifestsCount = 0;
    private voidedPayablesOrderPayablesCount = 0;
    private voidedPayablesDriverSettlementCount = 0;
    private payeeCacheCount = 0;
    private stateFederalTaxDriverSettlementCount = 0;

    // Timer interval for updating labels every 10 seconds
    private updateInterval: NodeJS.Timeout | null = null;

    onLoad() {
        // Parse window.location.search for comma-separated numbers
        if (window.location.search) {
            const params = new URLSearchParams(window.location.search);
            const numbersParam = params.get('numbers');

            if (numbersParam) {
                // Split by comma and trim whitespace
                const numbers = numbersParam.split(',').map(n => n.trim()).filter(n => n.length > 0);

                // Validate numbers and calculate total
                const validNumbers: number[] = [];
                let total = 0;

                for (const numStr of numbers) {
                    if (/^\d+$/.test(numStr)) {
                        const num = parseInt(numStr);
                        validNumbers.push(num);
                        total += num;
                    }
                }

                // Check if total exceeds 100
                if (total > 100) {
                    Snackbar.showWarningSnackbar("Total number of listeners should be 100 or less.");
                }

                // Array of textboxes in order
                const textboxes = [
                    this.textboxLibClientListCount,
                    this.textboxPowerbrokerBrokerageMovementsCount,
                    this.textboxDispatchAvailableMoveCount,
                    this.textboxDispatchEquipMoveCount,
                    this.textboxDatafusionLoadTenderExpressRemoteCount,
                    this.textboxSettlementOrderPayablesCacheCount,
                    this.textboxArInvoiceProcessingCacheCount,
                    this.textboxDispatchEnvironmentManagementMovementsCount,
                    this.textboxDispatchPartialShipmentPlanningOrdersCount,
                    this.textboxDispatchPartialShipmentPlanningManifestsCount,
                    this.textboxSettlementVoidedPayablesOrderPayablesCount,
                    this.textboxSettlementVoidedPayablesDriverSettlementCount,
                    this.textboxSettlementPayeeCacheCount,
                    this.textboxSettlementDriverSettlementStateFederalTaxCount
                ];

                // Fill textboxes positionally with valid numbers
                for (let i = 0; i < Math.min(validNumbers.length, textboxes.length); i++) {
                    textboxes[i].text = validNumbers[i].toString();
                }
            }
        }
    }

    private updateAllLabels() {
        this.labelLibClientListMessages.text = this.clientListCount.toString();
        this.labelPowerbrokerBrokerageMovementsMessages.text = this.brokerageMovementsCount.toString();
        this.labelDispatchAvailableMoveMessages.text = this.availableMoveCount.toString();
        this.labelDispatchEquipMoveMessages.text = this.equipMoveCount.toString();
        this.labelDatafusionLoadTenderExpressRemoteMessages.text = this.loadTenderExpressRemoteCount.toString();
        this.labelSettlementOrderPayablesCacheMessages.text = this.orderPayablesCacheCount.toString();
        this.labelArInvoiceProcessingCacheMessages.text = this.invoiceProcessingCacheCount.toString();
        this.labelDispatchEnvironmentManagementMovementsMessages.text = this.environmentManagementMovementsCount.toString();
        this.labelDispatchPartialShipmentPlanningOrdersMessages.text = this.partialShipmentPlanningOrdersCount.toString();
        this.labelDispatchPartialShipmentPlanningManifestsMessages.text = this.partialShipmentPlanningManifestsCount.toString();
        this.labelSettlementVoidedPayablesOrderPayablesMessages.text = this.voidedPayablesOrderPayablesCount.toString();
        this.labelSettlementVoidedPayablesDriverSettlementMessages.text = this.voidedPayablesDriverSettlementCount.toString();
        this.labelSettlementPayeeCacheMessages.text = this.payeeCacheCount.toString();
        this.labelSettlementDriverSettlementStateFederalTaxMessages.text = this.stateFederalTaxDriverSettlementCount.toString();
    }

    /** This is an event handler for the onClick event of buttonButton1. */
    buttonButton1OnClick(event: ClickEvent) {
        // Clear any existing interval
        if (this.updateInterval) {
            clearInterval(this.updateInterval);
        }

        // Start interval to update labels every 10 seconds
        this.updateInterval = setInterval(() => {
            this.updateAllLabels();
        }, 10000);

        // Create MessageSubscriber instances for each topic based on textbox values

        // lib/client-list
        const clientListSubscriberCount = parseInt(this.textboxLibClientListCount.text) || 0;
        for (let i = 0; i < clientListSubscriberCount; i++) {
            new MessageSubscriber("model-change-lib/client-list", false, (message: any) => {
                this.clientListCount++;
            });
        }

        // lme/powerbroker/brokerage-movements
        const brokerageMovementsSubscriberCount = parseInt(this.textboxPowerbrokerBrokerageMovementsCount.text) || 0;
        for (let i = 0; i < brokerageMovementsSubscriberCount; i++) {
            new MessageSubscriber("model-change-lme/powerbroker/brokerage-movements", false, (message: any) => {
                this.brokerageMovementsCount++;
                if( log.isDebugEnabled() ){
                    log.debug("Message "+this.brokerageMovementsCount);
                }
            });
        }

        // lme/dispatch/available-move
        const availableMoveSubscriberCount = parseInt(this.textboxDispatchAvailableMoveCount.text) || 0;
        for (let i = 0; i < availableMoveSubscriberCount; i++) {
            new MessageSubscriber("model-change-lme/dispatch/available-move", false, (message: any) => {
                this.availableMoveCount++;
            });
        }

        // lme/dispatch/equip-move
        const equipMoveSubscriberCount = parseInt(this.textboxDispatchEquipMoveCount.text) || 0;
        for (let i = 0; i < equipMoveSubscriberCount; i++) {
            new MessageSubscriber("model-change-lme/dispatch/equip-move", false, (message: any) => {
                this.equipMoveCount++;
            });
        }

        // lme/datafusion/load-tender-express-remote
        const loadTenderExpressRemoteSubscriberCount = parseInt(this.textboxDatafusionLoadTenderExpressRemoteCount.text) || 0;
        for (let i = 0; i < loadTenderExpressRemoteSubscriberCount; i++) {
            new MessageSubscriber("model-change-lme/datafusion/load-tender-express-remote", false, (message: any) => {
                this.loadTenderExpressRemoteCount++;
            });
        }

        // lme/settlement/order-payables-cache
        const orderPayablesCacheSubscriberCount = parseInt(this.textboxSettlementOrderPayablesCacheCount.text) || 0;
        for (let i = 0; i < orderPayablesCacheSubscriberCount; i++) {
            new MessageSubscriber("model-change-lme/settlement/order-payables-cache", false, (message: any) => {
                this.orderPayablesCacheCount++;
            });
        }

        // lme/ar/invoice-processing-cache
        const invoiceProcessingCacheSubscriberCount = parseInt(this.textboxArInvoiceProcessingCacheCount.text) || 0;
        for (let i = 0; i < invoiceProcessingCacheSubscriberCount; i++) {
            new MessageSubscriber("model-change-lme/ar/invoice-processing-cache", false, (message: any) => {
                this.invoiceProcessingCacheCount++;
            });
        }

        // lme/dispatch/environment-management-movements
        const environmentManagementMovementsSubscriberCount = parseInt(this.textboxDispatchEnvironmentManagementMovementsCount.text) || 0;
        for (let i = 0; i < environmentManagementMovementsSubscriberCount; i++) {
            new MessageSubscriber("model-change-lme/dispatch/environment-management-movements", false, (message: any) => {
                this.environmentManagementMovementsCount++;
            });
        }

        // lme/dispatch/partial-shipment-planning-orders
        const partialShipmentPlanningOrdersSubscriberCount = parseInt(this.textboxDispatchPartialShipmentPlanningOrdersCount.text) || 0;
        for (let i = 0; i < partialShipmentPlanningOrdersSubscriberCount; i++) {
            new MessageSubscriber("model-change-lme/dispatch/partial-shipment-planning-orders", false, (message: any) => {
                this.partialShipmentPlanningOrdersCount++;
            });
        }

        // lme/dispatch/partial-shipment-planning-manifests
        const partialShipmentPlanningManifestsSubscriberCount = parseInt(this.textboxDispatchPartialShipmentPlanningManifestsCount.text) || 0;
        for (let i = 0; i < partialShipmentPlanningManifestsSubscriberCount; i++) {
            new MessageSubscriber("model-change-lme/dispatch/partial-shipment-planning-manifests", false, (message: any) => {
                this.partialShipmentPlanningManifestsCount++;
            });
        }

        // lme/settlement/voided-payables-order-payables
        const voidedPayablesOrderPayablesSubscriberCount = parseInt(this.textboxSettlementVoidedPayablesOrderPayablesCount.text) || 0;
        for (let i = 0; i < voidedPayablesOrderPayablesSubscriberCount; i++) {
            new MessageSubscriber("model-change-lme/settlement/voided-payables-order-payables", false, (message: any) => {
                this.voidedPayablesOrderPayablesCount++;
            });
        }

        // lme/settlement/voided-payables-driver-settlement
        const voidedPayablesDriverSettlementSubscriberCount = parseInt(this.textboxSettlementVoidedPayablesDriverSettlementCount.text) || 0;
        for (let i = 0; i < voidedPayablesDriverSettlementSubscriberCount; i++) {
            new MessageSubscriber("model-change-lme/settlement/voided-payables-driver-settlement", false, (message: any) => {
                this.voidedPayablesDriverSettlementCount++;
            });
        }

        // lme/settlement/payee-cache
        const payeeCacheSubscriberCount = parseInt(this.textboxSettlementPayeeCacheCount.text) || 0;
        for (let i = 0; i < payeeCacheSubscriberCount; i++) {
            new MessageSubscriber("model-change-lme/settlement/payee-cache", false, (message: any) => {
                this.payeeCacheCount++;
            });
        }

        // lme/settlement/driver-settlement/state-federal-tax-driver-settlement
        const stateFederalTaxDriverSettlementSubscriberCount = parseInt(this.textboxSettlementDriverSettlementStateFederalTaxCount.text) || 0;
        for (let i = 0; i < stateFederalTaxDriverSettlementSubscriberCount; i++) {
            new MessageSubscriber("model-change-lme/settlement/driver-settlement/state-federal-tax-driver-settlement", false, (message: any) => {
                this.stateFederalTaxDriverSettlementCount++;
            });
        }

        // Calculate total number of subscribers created
        const totalSubscribers =
            clientListSubscriberCount +
            brokerageMovementsSubscriberCount +
            availableMoveSubscriberCount +
            equipMoveSubscriberCount +
            loadTenderExpressRemoteSubscriberCount +
            orderPayablesCacheSubscriberCount +
            invoiceProcessingCacheSubscriberCount +
            environmentManagementMovementsSubscriberCount +
            partialShipmentPlanningOrdersSubscriberCount +
            partialShipmentPlanningManifestsSubscriberCount +
            voidedPayablesOrderPayablesSubscriberCount +
            voidedPayablesDriverSettlementSubscriberCount +
            payeeCacheSubscriberCount +
            stateFederalTaxDriverSettlementSubscriberCount;

        Snackbar.showSnackbar(`${totalSubscribers} MessageSubscriber instances created`);
    }
}
